import { OrgDispatcher } from '@/components/common/org-dispatcher';
import { ApprovalPage } from '@/pages/approval-page';
import { APPROVAL_ENTRY_MENU_KEYS } from '@/lib/approval-client';
import { ProtectedRoute } from '@/components/common/protected-route';
import { Toaster } from '@/components/ui/toaster';
import { decodeOrgCode } from '@/lib/org-code';
import { DashboardPage } from '@/pages/dashboard-page';
import { MainPage } from '@/pages/main-page';
import { PendingApprovalPage } from '@/pages/pending-approval-page';
import { StationMapPage } from '@/pages/station-map-page';
import { UserApprovalPage } from '@/pages/admin/user-approval-page';
import { CompanyManagementPage } from '@/pages/admin/company-management-page';
import { Navigate, Route, Routes, useParams } from 'react-router-dom';

// :orgCode 프리픽스 검증 가드. 옛 무프리픽스 URL(/dashboard 등)이나 오타는
// orgCode로 바인딩되어 decodeOrgCode→null이 되므로 `/`(디스패처)로 되돌린다.
// `/`는 :orgCode가 아니므로 루프가 발생하지 않는다.
// 주의: 옛 무프리픽스 딥링크는 보존되지 않고 org 루트로 착지한다(내부 admin SPA로 수용).
function OrgScopedRoute({ children }: { children: React.ReactNode }) {
  const { orgCode } = useParams();
  if (!orgCode || !decodeOrgCode(orgCode)) {
    return <Navigate to='/' replace />;
  }
  return <>{children}</>;
}

export function App() {
  return (
    <>
      <Routes>
        {/* `/` 진입 → 인증 후 활성 org 경로로 정규화 (디스패처) */}
        <Route
          path='/'
          element={
            <ProtectedRoute>
              <OrgDispatcher />
            </ProtectedRoute>
          }
        />

        {/* Protected routes - 미인증 시 Keycloak 로그인으로 자동 리다이렉트 */}
        <Route
          path='/:orgCode'
          element={
            <ProtectedRoute>
              <OrgScopedRoute>
                <MainPage />
              </OrgScopedRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path='/:orgCode/dashboard'
          element={
            <ProtectedRoute requiredMenuId='dashboard'>
              <OrgScopedRoute>
                <DashboardPage />
              </OrgScopedRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path='/:orgCode/station-map'
          element={
            <ProtectedRoute>
              <OrgScopedRoute>
                <StationMapPage />
              </OrgScopedRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path='/:orgCode/admin/user/approval'
          element={
            <ProtectedRoute>
              <OrgScopedRoute>
                <UserApprovalPage />
              </OrgScopedRoute>
            </ProtectedRoute>
          }
        />
        {/* 회사(테넌트) 관리는 특정 조직에 속하지 않는 슈퍼어드민 기능이라 org 프리픽스를 쓰지 않는다 */}
        <Route
          path='/admin/company'
          element={
            <ProtectedRoute>
              <CompanyManagementPage />
            </ProtectedRoute>
          }
        />

        {/* 전자결재 SPA - 탭 쉘 밖 새 창. 내부 라우팅은 ApprovalPage 가 처리한다 */}
        <Route
          path='/:orgCode/approvals/*'
          element={
            <ProtectedRoute requiredMenuId={APPROVAL_ENTRY_MENU_KEYS}>
              <OrgScopedRoute>
                <ApprovalPage />
              </OrgScopedRoute>
            </ProtectedRoute>
          }
        />

        {/* Public routes - 무프리픽스 유지 (에러 상태) */}
        <Route path='/pending-approval' element={<PendingApprovalPage />} />

        {/* 나머지는 디스패처(`/`)로 */}
        <Route path='*' element={<Navigate to='/' replace />} />
      </Routes>

      <Toaster />
    </>
  );
}
